import { Avatar, Box, Card, CardContent, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { TFunction } from "i18next";
import OverviewCard from "../../../components/dashboard/OverviewCard";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DashboardContext } from "../../../contexts/DashboardContext";
import InfoPopper from "../../../components/popper/InfoPopper";
import OverviewCardDetail from "../../../components/dashboard/OverviewCardDetail";
import {
  ChartsItemContentProps,
  PieChart,
  useDrawingArea,
} from "@mui/x-charts";
import PieTooltip from "../../../components/chart/PieTooltip";
import PieDetail from "../../../components/chart/PieDetail";
import NumberFormatterUtils from "../../../utils/numberFormatterUtils";
import { REPORT_GROUP } from "../../../constants/report-group";
import EmissionByCard from "../../../components/dashboard/EmissionByCard";
import i18n, { common, overview } from "../../../i18n";
import {
  DashboardResults,
  EmissionSource,
  SCOPE_MAPPING,
} from "../../../models/emission-dashboard.types";
import EmissionByBusinessCardV2 from "../../../components/dashboard/EmissionByBusinessCard";
import Group from "../../../models/Group";
import EmissionCard, {
  byChart,
} from "../../../components/dashboard/EmissionCard";

function PieCenterLabel({
  t,
  value,
}: {
  t: TFunction<[string, string], undefined>;
  value: number;
}) {
  const { width, height, left, top } = useDrawingArea();
  const x = left + width / 2;
  return (
    <text
      textAnchor={"middle"}
      dominantBaseline={"central"}
      x={x}
      y={top + height / 2}
      fontFamily="Inter"
    >
      <tspan x={x} dy={"-24px"} fontSize={"12px"}>
        {t("Total")}
      </tspan>
      <tspan x={x} dy={"22px"} fontSize={"16px"}>
        {NumberFormatterUtils.formatDynamic(value)}
      </tspan>
      <tspan x={x} dy={"22px"} fontSize={"12px"}>
        tCO₂e
      </tspan>
    </text>
  );
}

export type ScopeType = "Scope-1" | "Scope-2" | "Scope-3" | "Outside-of-Scope";

interface Props {
  preview?: boolean;
  scopeId: ScopeType;
}

const ScopeDetail = ({ preview, scopeId }: Props) => {
  const { t } = useTranslation([common, overview], { nsMode: "fallback" });

  const context = useContext(DashboardContext);
  const dashboardData: DashboardResults | undefined = context.dashboardData;

  const group = REPORT_GROUP.filter((item) => item.scope === scopeId);

  // 1. Prepare Data
  const scopeKey = SCOPE_MAPPING[scopeId];
  const scopeDataV2 = dashboardData?.byScopes[scopeKey];

  const groupConfig = REPORT_GROUP.filter((item) => item.scope === scopeId);

  // 2. Pie Chart Series
  const seriesV2 =
    scopeDataV2?.byTypes.types
      .map((s, i) => {
        let color = groupConfig[i]?.colors[1] ?? "#cccccc";

        let title = i18n.language === "th" ? s.categoryTH : s.categoryEn;

        if (scopeId === "Scope-3") {
          const item = group.find((g) => g.id === s.categoryEn);

          title = t(item?.title ?? s.categoryEn);
          color = item?.colors[1] ?? color;
        }

        return {
          id: i,
          value: s.value,
          label: title,
          color: color,
          percent: s.percent,
        };
      })
      .sort((a, b) => b.value - a.value) ?? [];

  let detailsV2 = seriesV2.map((item, index) => {
    const title = item.label ? t(item.label) : "";

    return (
      <PieDetail
        key={`pieDetail${index}`}
        title={title}
        percent={item.percent ?? 0}
        value={item.value}
        color={item.color ?? "Outside-of-Scope"}
      />
    );
  });

  // 3. Asset & Source Records
  const byAssetRecordV2: Record<string, number> = {};
  const bySourceRecordV2: Record<string, number> = {};

  scopeDataV2?.byAssets.forEach((item) => {
    const asset = item.asset ?? "";
    byAssetRecordV2[asset] = item.value;
  });

  scopeDataV2?.bySources.forEach((item) => {
    const source = i18n.language === "th" ? item.categoryTH : item.categoryEn;
    bySourceRecordV2[source] = item.value;
  });

  // 4. Group Mapping for Emission Cards
  const [groupV2, setGroupV2] = useState<Group[]>([]);
  const [fullGroupV2, setFullGroupV2] = useState<Group[]>([]);

  useEffect(() => {
    if (!dashboardData || !scopeDataV2) return;

    const currentGroupMapping: Record<string, EmissionSource> = {};
    const fullGroupMapping: Record<string, EmissionSource> = {};

    scopeDataV2.monthly?.forEach((m) => {
      m.types?.forEach((t) => {
        fullGroupMapping[`${t.categoryEn}`] = t;
        currentGroupMapping[`${t.categoryEn}`] = t;
      });
      m.previous_1year.types?.forEach(
        (t) => (fullGroupMapping[`${t.categoryEn}`] = t),
      );
      m.previous_2year.types?.forEach(
        (t) => (fullGroupMapping[`${t.categoryEn}`] = t),
      );
    });

    const findColor = (categoryEn: string, index: number) => {
      return (
        groupConfig[index % groupConfig.length]?.colors || ["#ddd", "#999"]
      );
    };

    setGroupV2(
      Object.keys(currentGroupMapping).map((key, index) => {
        const item = currentGroupMapping[key];

        let title = i18n.language === "th" ? item.categoryTH : item.categoryEn;

        if (scopeId === "Scope-3") {
          const groupItem = group.find((g) => g.id === item.categoryEn);

          title = t(groupItem?.title ?? item.categoryEn);
        }
        return {
          id: item.categoryEn,
          title: title,
          colors: findColor(item.categoryEn, index),
        } as unknown as Group;
      }),
    );

    setFullGroupV2(
      Object.keys(fullGroupMapping).map((key, index) => {
        const item = fullGroupMapping[key];

        let title = i18n.language === "th" ? item.categoryTH : item.categoryEn;

        if (scopeId === "Scope-3") {
          const groupItem = group.find((g) => g.id === item.categoryEn);

          title = t(groupItem?.title ?? item.categoryEn);
        }

        return {
          id: item.categoryEn,
          title: title,
          colors: findColor(item.categoryEn, index),
        } as unknown as Group;
      }),
    );
  }, [dashboardData, scopeId, scopeKey, i18n.language]);

  const tooltip = (props: ChartsItemContentProps<any>) => {
    return PieTooltip(props, t);
  };

  // Dynamic Texts
  const scopeTitle = t(scopeId);
  const scopeDescKey = `${scopeId}.Summary-Description`;
  const categoriesTitle = t(`${scopeId}-Categories`);
  const categoriesDesc = t(`${scopeId}-Categories-Description`);
  const emissionsTitle = t(`${scopeId}-Emissions`);
  const emissionsDesc = t(`${scopeId}-Emissions-Desc`);

  // Dummy function for old prop compatibility (if needed)
  const getGroupDummy = () => "";

  return (
    <Box display={"flex"} flexDirection={"column"} gap={"12px"}>
      {/* SECTION 1: Overview Card */}
      <Grid container spacing={"20px"}>
        <Grid xs={preview ? undefined : 12} lg="auto">
          <OverviewCard
            title={scopeTitle}
            info={t(scopeDescKey)}
            text1={`${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes[scopeKey]?.percent ?? 0,
            )} %`}
            text2={`${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes[scopeKey]?.value ?? 0,
            )} / ${NumberFormatterUtils.formatDynamic(
              dashboardData?.sumScopes.total ?? 0,
            )} tCO₂e`}
            icon={`/img/${scopeId.toLowerCase().replace("-", "")}.svg`}
          />
        </Grid>
        <Grid xs>
          <Card variant="outlined" sx={{ height: "100%", borderRadius: "8px" }}>
            <CardContent sx={{ margin: "8px", px: { lg: "40px" } }}>
              <Grid
                container
                spacing={"40px"}
                justifyContent={{
                  xs: preview ? "space-around" : "left",
                  md: "left",
                }}
              >
                {scopeDataV2?.byTypes.types
                  .filter((i) => i.value > 0)
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 3)
                  .map((type) => (
                    <Grid
                      xs={preview ? 4 : 12}
                      sm={preview ? 4 : 6}
                      md={4}
                      key={type.categoryEn}
                    >
                      <OverviewCardDetail
                        title={
                          scopeId === "Scope-3"
                            ? t(
                                REPORT_GROUP.find(
                                  (i) => i.id === type.categoryEn,
                                )?.title ?? type.categoryEn,
                              )
                            : i18n.language === "th"
                            ? type.categoryTH
                            : type.categoryEn
                        }
                        text1={`${NumberFormatterUtils.formatDynamic(
                          type.percent,
                        )} %`}
                        text2={`${NumberFormatterUtils.formatDynamic(
                          type.value,
                        )} / ${NumberFormatterUtils.formatDynamic(
                          scopeDataV2.total,
                        )} tCO₂e`}
                      />
                    </Grid>
                  ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SECTION 2: Categories Pie Chart */}
      <Card variant="outlined" sx={{ borderRadius: "8px" }}>
        <CardContent sx={{ p: "40px" }}>
          <Box display={"flex"} flexDirection={"column"}>
            <Box display={"flex"} gap={"4px"} alignItems={"center"}>
              <Avatar
                src={`/img/${scopeId.toLowerCase().replace("-", "")}.svg`}
                sx={{ width: "34px", height: "34px" }}
              />
              <Typography variant="h3" flexGrow={1}>
                {categoriesTitle}
                <InfoPopper text={categoriesDesc} />
              </Typography>
            </Box>
            <Box
              display={"flex"}
              flexDirection={{ xs: "column", md: "row" }}
              gap={{ xs: "16px", md: "40px" }}
            >
              <Box flexShrink={0} display={"flex"}>
                <PieChart
                  series={[
                    {
                      data: seriesV2,
                      innerRadius: "50%",
                      outerRadius: "100%",
                    },
                  ]}
                  margin={{ top: 25, bottom: 25, left: 25, right: 25 }}
                  width={270}
                  height={270}
                  slotProps={{ legend: { hidden: true } }}
                  tooltip={{ trigger: "item", itemContent: tooltip }}
                >
                  <PieCenterLabel t={t} value={scopeDataV2?.total ?? 0} />
                </PieChart>
              </Box>
              <Box
                flexGrow={1}
                display={"flex"}
                flexDirection={{ xs: "column", lg: "row" }}
                gap={{ xs: "12px", lg: "40px" }}
              >
                <Box
                  flexGrow={1}
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"12px"}
                >
                  <Typography variant="h3" textAlign={"right"}>
                    {t("Total-Emissions")}
                  </Typography>
                  {detailsV2.slice(0, Math.round(detailsV2.length / 2))}
                </Box>
              </Box>

              {detailsV2.length > 1 && (
                <Box
                  flexGrow={1}
                  display={"flex"}
                  flexDirection={{ xs: "column", lg: "row" }}
                  gap={{ xs: "12px", lg: "40px" }}
                >
                  <Box
                    flexGrow={1}
                    display={"flex"}
                    flexDirection={"column"}
                    gap={"12px"}
                  >
                    <Typography variant="h3" textAlign={"right"}>
                      {t("Total-Emissions")}
                    </Typography>
                    {detailsV2.slice(Math.round(detailsV2.length / 2))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* SECTION 3: Emission Card (Bar Chart) */}
      <EmissionCard
        t={t}
        title={emissionsTitle}
        info={emissionsDesc}
        defaultBy={byChart}
        groups={fullGroupV2}
        startDate={context.startDate}
        endDate={context.endDate}
        preview={preview}
        data={dashboardData ?? null}
        scope={scopeId}
      />

      {/* SECTION 4: Emission By Business */}
      <EmissionByBusinessCardV2
        groups={groupV2}
        getGroup={getGroupDummy} // Mock function to satisfy prop types if needed
        scope={scopeId}
        // by={bySite} // Default
        // sort={highToLow} // Default
      />

      {/* SECTION 5: Emission By Asset/Source */}
      <Grid container spacing={"20px"}>
        <Grid xs={preview ? 6 : 12} md={6}>
          <EmissionByCard
            title={t("Emissions-by-Assets")}
            description={t("Emissions-by-Assets-Desc")}
            data={byAssetRecordV2}
            top={5}
          />
        </Grid>
        <Grid xs={preview ? 6 : 12} md={6}>
          <EmissionByCard
            title={t("Emissions-by-Sources")}
            description={t("Emissions-by-Sources-Desc")}
            data={bySourceRecordV2}
            top={5}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ScopeDetail;
